/**
 * Testimonials, from hung-hola blocks/testimonials/view.js (docs/animation/testimonials.md there):
 * - Video player (Figma 11544:32610): plays in place over the poster (the play button or a click on the frame), mute, full screen, a seek bar (click, drag,
 *   arrow keys); pauses when scrolled away. The buttons stay disabled until this runs (editor: the still player).
 * - The two card lists loop without end, in opposite directions (Figma prototype 11467:72926): columns move
 *   vertically on desktop, rows sideways at touch: (≤1280). gsap.utils.wrap, as the partners strip, so it never
 *   jumps back; cards are cloned (aria-hidden) until they cover the list; the first frame is the still layout.
 *   Shared speed (assets/js/loop-motion.ts): slower under the mouse, not touched by the page scroll (owner 2026-10-21).
 *   Pauses off screen.
 *   ?still and reduced motion keep the still lists (motionLib() is null).
 * - Fade-in: data-px-fade-in / data-px-fade-in-item in render.php, run site-wide (assets/js/fade-in.ts).
 */
import { isStill, motionLib } from '../../assets/js/motion';
import { loopMotion } from '../../assets/js/loop-motion';
import { getEasyPopup } from '../../assets/js/easy-popup-global';

const SPEED = 65; // px per second: Figma moves the cards 1586 px in 10 s (158.6); 110 in hung-hola, the client wants it slower (round 1 #12)
const DIRECTIONS = [-1, 1]; // per list: -1 up (desktop) / left (touch), 1 down / right
const SEEK_STEP = 5; // seconds per arrow key on the seek bar
const DESKTOP = '(width > 1280px)'; // columns; rows at touch:

function player(wrapper: HTMLElement): void {
	const video = wrapper.querySelector<HTMLVideoElement>('.testimonials__media');
	const playButton = wrapper.querySelector<HTMLButtonElement>('[data-testimonials-play]');
	const time = wrapper.querySelector<HTMLElement>('[data-testimonials-time]');
	const progress = wrapper.querySelector<HTMLElement>('[data-testimonials-progress]');
	const bar = wrapper.querySelector<HTMLElement>('[data-testimonials-bar]');
	if (!video || !(video.getAttribute('src') || video.querySelector('source[src]')) || !playButton || !time || !progress || !bar) {
		return;
	}
	const muteButton = wrapper.querySelector<HTMLButtonElement>('[data-testimonials-mute]');
	const fullscreenButton = wrapper.querySelector<HTMLButtonElement>('[data-testimonials-fullscreen]');

	const format = (seconds: number): string => {
		const s = Math.floor(seconds || 0);
		return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
	};
	// Each toggle button holds two icons: show the second one when `second`
	const swap = (button: HTMLButtonElement, second: boolean): void => {
		const [a, b] = button.querySelectorAll('img');
		if (a && b) {
			a.hidden = second;
			b.hidden = !second;
		}
	};
	const render = (): void => {
		const duration = Number.isFinite(video.duration) ? video.duration : 0;
		const ratio = duration ? video.currentTime / duration : 0;
		time.textContent = `${format(video.currentTime)} / ${format(duration)}`;
		bar.style.width = `${ratio * 100}%`;
		progress.setAttribute('aria-valuenow', String(Math.round(video.currentTime)));
		progress.setAttribute('aria-valuemax', String(Math.round(duration)));
		progress.setAttribute('aria-valuetext', time.textContent);
	};
	const renderState = (): void => {
		swap(playButton, !video.paused);
		playButton.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
		if (muteButton) {
			swap(muteButton, !video.muted);
			muteButton.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
		}
	};
	const seek = (seconds: number): void => {
		if (!video.duration) {
			return;
		}
		video.currentTime = Math.min(Math.max(seconds, 0), video.duration);
		render();
	};
	const seekTo = (clientX: number): void => {
		const box = progress.getBoundingClientRect();
		seek(((clientX - box.left) / box.width) * video.duration);
	};

	video.preload = 'metadata'; // the real duration in the time label before the first play
	[playButton, muteButton, fullscreenButton].forEach((button) => {
		if (button) {
			button.disabled = false;
		}
	});
	progress.classList.add('is-enabled', 'cursor-pointer');
	progress.setAttribute('role', 'slider');
	progress.setAttribute('aria-label', 'Seek');
	progress.setAttribute('aria-valuemin', '0');
	progress.tabIndex = 0;

	const toggle = (): void => {
		video.hidden = false;
		if (video.paused || video.ended) {
			video.play().catch(() => {});
		} else {
			video.pause();
		}
	};
	playButton.addEventListener('click', toggle);
	// A click anywhere on the frame toggles too (round 1 #14), except on the player bar with its own controls
	const frame = video.closest<HTMLElement>('.testimonials__video');
	frame?.classList.add('cursor-pointer');
	frame?.addEventListener('click', (e) => {
		if (!(e.target as Element).closest('.testimonials__player')) {
			toggle();
		}
	});
	muteButton?.addEventListener('click', () => (video.muted = !video.muted));
	fullscreenButton?.addEventListener('click', () => {
		video.hidden = false;
		if (video.requestFullscreen) {
			video.requestFullscreen().catch(() => {});
		} else {
			(video as HTMLVideoElement & { webkitEnterFullscreen?: () => void }).webkitEnterFullscreen?.(); // iOS Safari
		}
	});
	progress.addEventListener('pointerdown', (e) => {
		progress.setPointerCapture(e.pointerId);
		video.hidden = false;
		seekTo(e.clientX);
	});
	progress.addEventListener('pointermove', (e) => {
		if (progress.hasPointerCapture(e.pointerId)) {
			seekTo(e.clientX);
		}
	});
	progress.addEventListener('keydown', (e) => {
		const step = ({ ArrowRight: SEEK_STEP, ArrowUp: SEEK_STEP, ArrowLeft: -SEEK_STEP, ArrowDown: -SEEK_STEP } as Record<string, number>)[e.key];
		if (step === undefined) {
			return;
		}
		e.preventDefault();
		seek(video.currentTime + step);
	});
	['play', 'pause', 'ended', 'volumechange'].forEach((type) => video.addEventListener(type, renderState));
	['timeupdate', 'loadedmetadata', 'durationchange', 'seeked'].forEach((type) => video.addEventListener(type, render));
	new IntersectionObserver(([entry]) => {
		if (!entry.isIntersecting) {
			video.pause();
		}
	}).observe(video);
	renderState();
}

interface ListLoop {
	list: HTMLElement;
	direction: number;
	tween: gsap.core.Tween | null;
}

function loops(wrapper: HTMLElement): void {
	const lists = [...wrapper.querySelectorAll<HTMLElement>('[data-testimonials-list]')];
	const lib = motionLib();
	if (!lists.length || !lib) {
		return;
	}
	const { gsap } = lib;
	const desktop = window.matchMedia(DESKTOP);
	// One set = the real cards of list 1
	const period = lists[0].querySelectorAll(':scope > li:not([aria-hidden])').length || 1;
	let inView = false;

	const all: ListLoop[] = lists.map((list, index) => ({ list, direction: DIRECTIONS[index] ?? -1, tween: null }));
	all.forEach((loop) => loopMotion()?.add({ hover: loop.list, scroll: false, apply: (scale) => loop.tween?.timeScale(scale) }));

	const play = (loop: ListLoop): void => {
		if (!loop.tween) {
			return;
		}
		if (inView) {
			loop.tween.play();
		} else {
			loop.tween.pause();
		}
	};

	const stop = (loop: ListLoop): void => {
		loop.tween?.kill();
		loop.tween = null;
		loop.list.querySelectorAll('[data-testimonials-clone]').forEach((clone) => clone.remove());
		gsap.set(loop.list.children, { clearProps: 'transform' });
	};

	const build = (loop: ListLoop): void => {
		const progress = loop.tween ? loop.tween.progress() : 0;
		stop(loop);

		const { list, direction } = loop;
		const axis = desktop.matches ? 'y' : 'x';
		const items = [...list.children] as HTMLElement[];
		if (items.length <= period) {
			return;
		}

		const listStart = (): number => list.getBoundingClientRect()[axis === 'y' ? 'top' : 'left'];
		const start = (item: Element): number => item.getBoundingClientRect()[axis === 'y' ? 'top' : 'left'] - listStart();
		const end = (item: Element): number => item.getBoundingClientRect()[axis === 'y' ? 'bottom' : 'right'] - listStart();
		const size = axis === 'y' ? list.clientHeight : list.clientWidth;
		const set = start(items[period]) - start(items[0]);
		if (!set) {
			return;
		}
		const before = start(items[0]);

		// Enough cards for the worst point of the loop: one set past the visible part of the list
		while (end(items[items.length - 1]) - start(items[0]) < size + set) {
			const copy = items[items.length - period].cloneNode(true) as HTMLElement;
			copy.setAttribute('aria-hidden', 'true');
			copy.setAttribute('data-testimonials-clone', '');
			copy.querySelectorAll('img').forEach((img) => (img.alt = ''));
			list.append(copy);
			items.push(copy);
		}

		// A centred list moves when cards are added: shift back so the first frame is the still layout
		const first = start(items[0]);
		const total = end(items[items.length - 1]) - first;
		const offset = before - first;
		// Any offset in [low, high] keeps the list covered; wrap around the middle of that space
		const low = size - first - total;
		const high = -first - set;
		const from = (low + high) / 2;
		const wrap = gsap.utils.wrap(from, from + set);

		gsap.set(items, { [axis]: wrap(offset + direction * progress * set) });
		loop.tween = gsap.to(items, {
			[axis]: `${direction > 0 ? '+=' : '-='}${set}`,
			duration: set / SPEED,
			ease: 'none',
			repeat: -1,
			modifiers: { [axis]: gsap.utils.unitize(wrap) },
			paused: true,
		});
		play(loop);
	};

	const buildAll = (): void => all.forEach(build);

	// Rebuild when the lists change size (breakpoint, window width)
	let frame = 0;
	const resizeObserver = new ResizeObserver(() => {
		cancelAnimationFrame(frame);
		frame = requestAnimationFrame(buildAll);
	});
	lists.forEach((list) => resizeObserver.observe(list));

	new IntersectionObserver(([entry]) => {
		inView = entry.isIntersecting;
		all.forEach(play);
	}).observe(lists[0].parentElement ?? lists[0]);
}

// YouTube video (the "YouTube video" field, 2026-10-26): the card keeps the poster; the play button or a click on the
// card opens its Easy Popup (render.php) with YouTube's player (youtube-nocookie, autoplay), so the landscape video
// shows whole. Nothing from YouTube loads before; closing (Easy Popup's ✕, Escape, a click outside) removes the player,
// which stops it. Easy Popup stops Lenis while it is open.
function youtube(wrapper: HTMLElement): void {
	const frame = wrapper.querySelector<HTMLElement>('[data-testimonials-youtube]');
	const playButton = frame?.querySelector<HTMLButtonElement>('[data-testimonials-play]');
	const id = frame?.dataset.testimonialsYoutube;
	const popup = getEasyPopup(frame?.dataset.testimonialsPopup);
	const box = popup?.el.querySelector<HTMLElement>('.testimonials-popup__video');
	if (!frame || !playButton || !id || !popup || !box) {
		return;
	}
	const title = frame.dataset.testimonialsTitle || 'Testimonial video';

	popup.on('onOpen', () => {
		const player = document.createElement('iframe');
		const params = new URLSearchParams({ autoplay: '1', playsinline: '1', rel: '0' });
		player.src = `https://www.youtube-nocookie.com/embed/${id}?${params}`;
		player.title = title;
		player.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
		player.allowFullscreen = true;
		box.replaceChildren(player);
	});
	popup.on('onClose', () => box.replaceChildren());

	// The card's muted loop (the uploaded Video), only while on screen and the popup is shut; ?still and reduced motion keep the poster
	const preview = frame.querySelector<HTMLVideoElement>('[data-testimonials-preview]');
	if (preview && !isStill()) {
		let inView = false;
		const update = (): void => {
			if (inView && !popup.isOpen) {
				preview.play().catch(() => {});
			} else {
				preview.pause();
			}
		};
		new IntersectionObserver(([entry]) => {
			inView = entry.isIntersecting;
			update();
		}).observe(frame);
		popup.on('onOpen', update);
		popup.on('onClose', update);
	}

	playButton.disabled = false;
	frame.classList.add('cursor-pointer');
	frame.addEventListener('click', () => popup.open());
}

document.querySelectorAll<HTMLElement>('[data-px-block="testimonials"]').forEach((wrapper) => {
	player(wrapper);
	youtube(wrapper);
	loops(wrapper);
});
